<template>
  <custom-v-input
      :label="hideLabel ? null : label"
      :flat="flat"
      :hide-details="hideDetailsView"
      :dense="dense"
      :prepend-icon="prependIcon"
      :append-icon="appendIcon"
      :messages="messagesView.map(v => v.text)"
      :class="inputClass"
      readonly>
    <template #default>
      <div :class="{
        ...colorClass,
        'text-field-like__slot': true,
      }">
        <span :class="{
          'text-field-like__view': true,
          'text-field-like__placeholder': !value && !!placeholder,
          'text-field-like__multi-line': multiLine,
        }">
          <slot v-bind:value="valueView">{{ valueView }}</slot>
        </span>
      </div>
      <v-progress-linear
          v-if="loading"
          color="primary"
          height="2"
          style="position: relative; top: -1px;"
          bottom
          indeterminate>
      </v-progress-linear>
    </template>
    <template #prepend>
      <slot name="prepend"></slot>
    </template>
    <template #append>
      <slot name="append"></slot>
    </template>
    <template #label>
      <slot name="label"></slot>
    </template>
    <template #message>
      <template v-for="(msg, index) in messagesView">
        <local-id-box
            v-if="msg.type === 'localId'"
            :key="index"
            :value="msg.text"/>
        <hds-id-box
            v-else-if="msg.type === 'hdsId'"
            :key="index"
            :value="msg.text"/>
        <div v-else :key="index">{{ msg.text }}</div>
      </template>
    </template>
  </custom-v-input>
</template>

<script>
import Vue from "vue";
import {VInput} from "vuetify/lib";
import VLabel from "vuetify/lib/components/VLabel/VLabel";
import HdsIdBox from "@/components/molecules/HdsIdBox.vue";
import LocalIdBox from "@/components/molecules/LocalIdBox.vue";

const CustomVInput = Vue.extend(VInput).extend({
  computed: {
    themeClasses() {
      return {
        'theme--dark': this.dark === true,
        'theme--light': this.light === true,
      }
    },
  },
  methods: {
    genLabel() {
      if (!this.hasLabel) return null

      return this.$createElement(VLabel, {
        props: {
          absolute: true,
          color: this.validationState,
          dark: this.dark,
          disabled: this.isDisabled,
          focused: this.hasState,
          for: this.computedId,
          light: this.light,
          value: true
        },
      }, this.$slots.label || this.label);
    }
  }
});

function valueToString(value) {
  if(value === null || value === undefined) {
    return '';
  } else {
    return '' + value;
  }
}

function valueToArray(value) {
  if(Array.isArray(value)) {
    return value.map(v => valueToString(v)).filter(v => !!v);
  } else {
    value = valueToString(value);
    return value ? [value] : [];
  }
}

export default {
  name: "TextFieldLike",
  components: {
    LocalIdBox,
    HdsIdBox,
    CustomVInput
  },
  props: {
    value: {
      required: false
    },
    label: {
      type: String,
      required: false
    },
    flat: {
      type: Boolean,
      required: false,
      default: false
    },
    hideDetails: {
      type: Boolean,
      required: false,
      default: false
    },
    hideLabel: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: 'black'
    },
    placeholder: {
      type: String
    },
    dense: {
      type: Boolean,
      default: false
    },
    align: { // 'start' | 'center' | 'end'
      type: String,
      default: 'start'
    },
    loading: {
      type: Boolean,
      default: false
    },
    multiLine: {
      type: Boolean,
      default: false
    },
    prependIcon: {
      type: String
    },
    appendIcon: {
      type: String
    },
    valueId: {
      type: [String, Array],
    },
    valueLocalId: {
      type: [String, Array],
    },
    valueHdsId: {
      type: [String, Array],
    },
    message: {
      type: [String, Array],
    },
    messages: {
      type: [String, Array],
    },
  },
  data() {
    return {
      isFocused: false
    }
  },
  computed: {
    colorClass() {
      let color = this.color.trim();
      if(color === 'inherit') {
        return {'inherit--text': true};
      }
      return this.color
          .split(/\s+/)
          .map(c => c.trim())
          .filter(c => !!c)
          .map(c => c.match(/^(lighten|darken)-\d$/) ? `text--${c}` : `${c}--text`)
          .reduce((obj, c) => { obj[c] = true; return obj; }, {});
    },
    inputClass() {
      const cls = {'v-text-field': true, 'text-field-like__hide-label': this.hideLabel};
      switch(this.align) {
        case 'start':
        case 'left':
          cls['align-start'] = true;
          break;
        case 'center':
          cls['align-center'] = true;
          break;
        case 'end':
        case 'right':
          cls['align-end'] = true;
          break;
      }
      return cls;
    },
    valueView() {
      if(this.loading) {
        return ' ';
      }
      const value = (valueToString(this.value) || valueToString(this.placeholder) || '').trimRight();
      if(value) {
        return value;
      }
      return ' ';
    },
    messagesView() {
      const view = [];
      if(this.valueId) {
        valueToArray(this.valueId).forEach(v => view.push({type: 'id', text: v}));
      }
      if(this.valueLocalId) {
        valueToArray(this.valueLocalId).forEach(v => view.push({type: 'localId', text: v}));
      }
      if(this.valueHdsId) {
        valueToArray(this.valueHdsId).forEach(v => view.push({type: 'hdsId', text: v}));
      }
      if(this.message) {
        valueToArray(this.message).forEach(v => view.push({type: 'message', text: v}));
      }
      if(this.messages) {
        valueToArray(this.messages).forEach(v => view.push({type: 'message', text: v}));
      }
      return view;
    },
    hideDetailsView() {
      if(this.messagesView.length > 0) {
        return false;
      }
      return this.hideDetails;
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep {
  & > .v-input__control > .v-input__slot {
    flex-direction: column;
    align-items: flex-start;
    cursor: unset !important;

    .align-start & {
      align-items: flex-start;
    }

    .align-center & {
      align-items: center;
    }

    .align-end & {
      align-items: flex-end;
    }
  }

  .text-field-like__slot {
    .v-input:not(.v-input--dense) & {
      line-height: 20px;
      padding: 8px 0 4px;
      //max-height: 32px;
    }

    //&.text-field-like__slot__multi-line {
    //  max-height: none !important;
    //}

    .text-field-like__view {
      white-space: normal;

      &.text-field-like__multi-line {
        white-space: pre-wrap !important;
      }

      &.text-field-like__placeholder {
        opacity: 0.4;
      }

      &:empty:before {
        content: "\200b";
      }
    }

    .inherit--text {
      color: inherit;
    }
  }

  .v-messages {
    margin-top: -8px;
  }

  .v-messages__message > *:not(:first-child) {
    margin-top: 0.3rem;
  }

  &.v-input--dense .v-messages {
    margin-top: -4px;
    font-size: 80%;
    font-weight: normal;
  }
}

.text-field-like__hide-label {
  padding-top: 0;
}

.v-input--hide-details ::v-deep {
  .v-input__prepend-outer, .v-input__append-outer {
    margin-top: 0;
    margin-bottom: 0;
  }
}
</style>
